import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Typography,
  Button,
  Table,
  Input,
  Select,
  Tag,
  Space,
  Drawer,
  Form,
  Switch,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
} from '@ant-design/icons';
import NegativeProposalReasonService, {
  INegativeProposalReason,
} from '../../services/NegativeProposalReasonService';
import { formatDate, openNotification } from '../../utils';
import PenIcon from 'assets/icons/pen.svg';
import TrashIcon from 'assets/icons/trash.svg';

const { Title, Text } = Typography;
const { Option } = Select;

const NegativeProposalReasonList: React.FC = () => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
  });
  const [reasons, setReasons] = useState<INegativeProposalReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [pendingVisibilityFilter, setPendingVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');
  const [pendingActiveFilter, setPendingActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedReason, setSelectedReason] = useState<INegativeProposalReason | null>(null);
  const [toggleLoadingId, setToggleLoadingId] = useState<number | null>(null);

  const [form] = Form.useForm();

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    if (pagination.current !== 1) {
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, [debouncedSearch, visibilityFilter, activeFilter]);

  const buildFilters = useCallback(() => {
    const params: Record<string, any> = {
      page: pagination.current - 1,
      size: pagination.pageSize,
    };
    if (debouncedSearch) {
      params['text.contains'] = debouncedSearch;
    }
    if (visibilityFilter === 'public') {
      params['isPublic.equals'] = true;
    } else if (visibilityFilter === 'private') {
      params['isPublic.equals'] = false;
    }
    if (activeFilter === 'active') {
      params['isActive.equals'] = true;
    } else if (activeFilter === 'inactive') {
      params['isActive.equals'] = false;
    }
    return params;
  }, [pagination.current, pagination.pageSize, debouncedSearch, visibilityFilter, activeFilter]);

  const fetchReasons = useCallback(() => {
    setLoading(true);
    const params = buildFilters();
    NegativeProposalReasonService.list(params)
      .then((response) => {
        setReasons(response.data.content);
        setPagination((prev) => ({
          ...prev,
          total: response.data.totalElements,
        }));
      })
      .catch((error) => {
        console.error('Negative reasons fetch failed', error);
        openNotification('error', 'Hata', 'Olumsuz teklif nedenleri listelenirken hata oluştu.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [buildFilters]);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  const handleTableChange = (newPagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current ?? prev.current,
      pageSize: newPagination.pageSize ?? prev.pageSize,
    }));
  };

  const openCreateDrawer = () => {
    setSelectedReason(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      isPublic: false,
      isOther: false,
    });
    setDrawerVisible(true);
  };

  const openEditDrawer = (reason: INegativeProposalReason) => {
    setSelectedReason(reason);
    form.setFieldsValue({
      text: reason.text,
      isActive: reason.isActive,
      isPublic: reason.isPublic,
      isOther: reason.isOther,
    });
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedReason(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      if (selectedReason) {
        await NegativeProposalReasonService.update(selectedReason.id, values);
        openNotification('success', 'Başarılı', 'Olumsuz teklif nedeni güncellendi.');
      } else {
        await NegativeProposalReasonService.create(values);
        openNotification('success', 'Başarılı', 'Olumsuz teklif nedeni oluşturuldu.');
      }
      closeDrawer();
      fetchReasons();
    } catch (error) {
      console.error('Failed to save reason', error);
      openNotification('error', 'Hata', 'Kaydetme sırasında hata oluştu.');
    }
  };

  const handleDelete = async (record: INegativeProposalReason) => {
    try {
      await NegativeProposalReasonService.remove(record.id);
      openNotification('success', 'Başarılı', 'Olumsuz teklif nedeni silindi.');
      fetchReasons();
    } catch (error) {
      console.error('Failed to delete reason', error);
      openNotification('error', 'Hata', 'Silme sırasında hata oluştu.');
    }
  };

  const handleToggleActive = async (record: INegativeProposalReason, checked: boolean) => {
    setToggleLoadingId(record.id);
    try {
      await NegativeProposalReasonService.update(record.id, {
        text: record.text,
        isActive: checked,
        isPublic: record.isPublic,
        isOther: record.isOther,
      });
      openNotification('success', 'Başarılı', 'Durum güncellendi.');
      fetchReasons();
    } catch (error) {
      console.error('Failed to toggle active', error);
      openNotification('error', 'Hata', 'Durum güncellenirken hata oluştu.');
    } finally {
      setToggleLoadingId(null);
    }
  };

  const handleToggleVisibility = async (record: INegativeProposalReason, checked: boolean) => {
    setToggleLoadingId(record.id);
    try {
      await NegativeProposalReasonService.update(record.id, {
        text: record.text,
        isActive: record.isActive,
        isPublic: checked,
        isOther: record.isOther,
      });
      openNotification('success', 'Başarılı', 'Görünürlük güncellendi.');
      fetchReasons();
    } catch (error) {
      console.error('Failed to toggle visibility', error);
      openNotification('error', 'Hata', 'Görünürlük güncellenirken hata oluştu.');
    } finally {
      setToggleLoadingId(null);
    }
  };

  const handleToggleOther = async (record: INegativeProposalReason, checked: boolean) => {
    setToggleLoadingId(record.id);
    try {
      await NegativeProposalReasonService.update(record.id, {
        text: record.text,
        isActive: record.isActive,
        isPublic: record.isPublic,
        isOther: checked,
      });
      openNotification('success', 'Başarılı', 'Diğer ayarı güncellendi.');
      fetchReasons();
    } catch (error) {
      console.error('Failed to toggle other reason flag', error);
      openNotification('error', 'Hata', 'Diğer ayarı güncellenirken hata oluştu.');
    } finally {
      setToggleLoadingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Neden',
        dataIndex: 'text',
        key: 'text',
        render: (text: string) => <Text>{text}</Text>,
      },
      {
        title: 'Durum',
        dataIndex: 'isActive',
        key: 'isActive',
        render: (_: boolean, record: INegativeProposalReason) => (
          <Switch
            checked={record.isActive}
            checkedChildren="Aktif"
            unCheckedChildren="Pasif"
            loading={toggleLoadingId === record.id}
            onChange={(checked) => handleToggleActive(record, checked)}
          />
        ),
      },
      {
        title: 'Kullanıcı Görünürlük',
        dataIndex: 'isOther',
        key: 'isOther',
        render: (_: boolean, record: INegativeProposalReason) => (
          <Switch
            checked={record.isOther}
            checkedChildren="Evet"
            unCheckedChildren="Hayır"
            loading={toggleLoadingId === record.id}
            onChange={(checked) => handleToggleOther(record, checked)}
          />
        ),
      },
      {
        title: 'Müşteri Mail Görünürlük',
        dataIndex: 'isPublic',
        key: 'isPublic',
        render: (_: boolean, record: INegativeProposalReason) => (
          <Switch
            checked={record.isPublic}
            checkedChildren="Genel"
            unCheckedChildren="Özel"
            loading={toggleLoadingId === record.id}
            onChange={(checked) => handleToggleVisibility(record, checked)}
          />
        ),
      },
      {
        title: 'Son Güncelleme',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value: string) => (value ? formatDate(value) : '-'),
      },
      {
        title: '',
        key: 'actions',
        align: 'right' as const,
        render: (_: any, record: INegativeProposalReason) => (
          <Space size={12}>
            <Tooltip title="Düzenle">
              <Button
                shape="circle"
                className="icon-button"
                onClick={() => openEditDrawer(record)}
                icon={<img src={PenIcon} alt="pen" className="icon" />}
              />
            </Tooltip>
            <Popconfirm
              title="Bu nedeni silmek istediğinize emin misiniz?"
              onConfirm={() => handleDelete(record)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Tooltip title="Sil">
                <Button
                  shape="circle"
                  className="icon-button ml-12px"
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [openEditDrawer],
  );

  return (
    <Card className="br-30px">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Row justify="space-between" align="middle" className="mb-16px">
            <div>
              <Title style={{ margin: 0, display: 'inline-block' }} level={4}>
                Olumsuz Teklif Nedenleri
              </Title>
              <Text type="secondary" className="ms-5">
                <strong>({pagination.total})</strong> kayıt listelendi
              </Text>
            </div>
            <Row align="middle">
              <Input
                allowClear
                placeholder="Ara"
                prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                className="d-input d-input-form d-input-filter ml-16px"
                style={{ width: 200 }}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <Button
                type="primary"
                className="d-button width-auto ml-10px dark-button"
                onClick={() => {
                  setPendingActiveFilter(activeFilter);
                  setPendingVisibilityFilter(visibilityFilter);
                  setFilterDrawerVisible(true);
                }}
              >
                Filtrele
              </Button>
              <Button
                type="primary"
                className="d-button width-auto ml-10px dark-button"
                onClick={openCreateDrawer}
              >
                Yeni Olumsuz Teklif Nedeni Ekle
              </Button>
            </Row>
          </Row>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={reasons}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50'],
          }}
          loading={loading}
          onChange={handleTableChange}
        />

      </Space>

      <Drawer
        title="Filtre"
        open={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
        destroyOnClose
        width={420}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text>Durum</Text>
            <Select
              value={pendingActiveFilter}
              onChange={(value) => setPendingActiveFilter(value)}
              style={{ width: '100%', marginTop: 8 }}
            >
              <Option value="all">Hepsi</Option>
              <Option value="active">Aktif</Option>
              <Option value="inactive">Pasif</Option>
            </Select>
          </div>
          <div>
            <Text>Görünürlük</Text>
            <Select
              value={pendingVisibilityFilter}
              onChange={(value) => setPendingVisibilityFilter(value)}
              style={{ width: '100%', marginTop: 8 }}
            >
              <Option value="all">Hepsi</Option>
              <Option value="public">Genel</Option>
              <Option value="private">Özel</Option>
            </Select>
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button
              onClick={() => {
                setPendingActiveFilter('active');
                setPendingVisibilityFilter('all');
                setActiveFilter('active');
                setVisibilityFilter('all');
                setFilterDrawerVisible(false);
              }}
            >
              Temizle
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setActiveFilter(pendingActiveFilter);
                setVisibilityFilter(pendingVisibilityFilter);
                setFilterDrawerVisible(false);
              }}
            >
              Filtrele
            </Button>
          </Space>
        </Space>
      </Drawer>

      <Drawer
        title={selectedReason ? 'Olumsuz Teklif Nedeni Düzenle' : 'Yeni Olumsuz Teklif Nedeni'}
        open={drawerVisible}
        onClose={closeDrawer}
        width={420}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        initialValues={{
          isActive: true,
          isPublic: false,
          isOther: false,
        }}
      >
          <Form.Item
            name="text"
            label="Neden"
            rules={[{ required: true, message: 'Lütfen neden metnini girin.' }]}
          >
            <Input.TextArea rows={4} placeholder="" />
          </Form.Item>
          <Form.Item name="isActive" label="Durum" valuePropName="checked">
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>
          <Form.Item name="isPublic" label="Görünürlük" valuePropName="checked">
            <Switch checkedChildren="Genel" unCheckedChildren="Özel" />
          </Form.Item>
          <Form.Item name="isOther" label="Diğer" valuePropName="checked">
            <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
          </Form.Item>
          <Form.Item>
            <Button block type="primary" htmlType="submit">
              {selectedReason ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
};

export default NegativeProposalReasonList;
